import { isCallable } from '@afterpay/utils'
import clsx from 'clsx'
import React, { ChangeEvent, forwardRef, KeyboardEventHandler, MouseEvent, ReactNode, RefObject, useContext, useEffect, useState } from 'react'

import { Override, ShellOption } from '../Option'
import { useThemeClass } from '../Theme'
import { Span } from '../Typography'
import { ICON, KIND, LABEL_PLACEMENT, LABEL_WIDTH, POLYLINE_MAP, SIZES, TEXT_MAP, VIEWBOX_MAP } from './constants'
import { RadioGroupContext } from './RadioGroupContext'
import styles from './style.module.scss'
import { LabelPlacement, LabelWidth, RadioButtonSizes, RadioIcon, RadioKind } from './types'

export interface RadioButtonProps {
  value: string
  checked?: boolean
  defaultChecked?: boolean
  disabled?: boolean
  id?: string
  indent?: boolean
  kind?: RadioKind
  icon?: RadioIcon
  label?: string | (() => ReactNode)
  name?: string
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void
  onClick?: (e: MouseEvent<HTMLInputElement>) => void
  onKeyDown?: KeyboardEventHandler<HTMLInputElement>
  reversed?: boolean
  size?: RadioButtonSizes
  tabIndex?: number
  'aria-label'?: string
  'aria-labelledby'?: string
  'aria-describedby'?: string
  testNameSpace?: string
  labelWidth?: LabelWidth
  labelPlacement?: LabelPlacement
  selectedBorder?: boolean
  roundedBorder?: boolean
  optionOverride?: Override | Override[]
  itemRef?: RefObject<HTMLDivElement> // ref for the radio button container
}

export const RadioButton = forwardRef<HTMLInputElement, RadioButtonProps>((props, ref) => {
  const {
    defaultChecked = false,
    id = Math.random().toString(36),
    label = '',
    onChange,
    onClick,
    selectedBorder = false,
    roundedBorder = false,
    optionOverride,
    value,
    itemRef,
    reversed: defaultReversed = false,
    kind: defaultKind = KIND.DEFAULT,
    icon: defaultIcon = ICON.DEFAULT,
    name: defaultName,
    testNameSpace: defaultTestNameSpace = '',
    indent: defaultIndent,
    size: defaultSize = SIZES.MEDIUM,
    labelWidth: defaultLabelWidht = LABEL_WIDTH.DEFAULT,
    labelPlacement: defaultLabelPlacement = LABEL_PLACEMENT.TOP,
    ...otherProps
  } = props
  // context is used to get the default values from RadioGroup
  const groupContext = useContext(RadioGroupContext)
  const {
    size = defaultSize,
    name = defaultName,
    disabled = props.disabled ?? false, // Radio Button disabled prop takes precedence over RadioGroup
    kind = defaultKind,
    icon = defaultIcon,
    reversed = defaultReversed,
    indent = defaultIndent,
    labelWidth = defaultLabelWidht,
    labelPlacement = defaultLabelPlacement,
    testNameSpace = defaultTestNameSpace,
  } = { ...groupContext, ...props }

  const hasCheckedProp = 'checked' in props
  const hasValuePropInGroupContext = 'value' in groupContext
  const initialChecked = props.checked ?? defaultChecked
  const [isChecked, setChecked] = useState(hasValuePropInGroupContext ? groupContext.value === value : initialChecked)

  const themeClass = useThemeClass()

  const labelClasses = clsx(
    styles[`size${size}`],
    styles.label,
    disabled && styles.disabled,
    reversed && styles.reversed,
    labelWidth === LABEL_WIDTH.FULL && styles.labelFullWidth,
    labelPlacement === LABEL_PLACEMENT.MIDDLE && styles.alignMiddle,
    icon === ICON.SELECT && styles.selectKind
  )
  const textClasses = clsx(styles.text, labelWidth === LABEL_WIDTH.FULL && styles.labelFullWidth, styles[themeClass])
  const hasTestId = testNameSpace.length > 0
  const containerClassNames = clsx(
    styles.container,
    indent === true && styles.indent,
    kind === KIND.OPTION && styles.optionKind,
    selectedBorder && styles.containerBoldBorder,
    roundedBorder && styles.containerRoundedBorder,
    styles[`${themeClass}`]
  )

  const handleChange = (e: ChangeEvent<HTMLInputElement>): void => {
    if (!hasCheckedProp && !hasValuePropInGroupContext) {
      setChecked(e.target.checked)
    }
    onChange?.(e)
    groupContext?.onChange?.(e)
  }

  const handleClick = (e: MouseEvent<HTMLInputElement>): void => {
    onClick?.(e)
    groupContext?.onClick?.(e)
  }

  useEffect(() => {
    if (hasValuePropInGroupContext) {
      setChecked(groupContext.value === value)
    } else if (hasCheckedProp) {
      setChecked(props.checked ?? false)
    }
  }, [props.checked, groupContext.value, value, hasValuePropInGroupContext, hasCheckedProp])

  const renderTick = (): boolean | JSX.Element => {
    return (
      icon === KIND.SELECT && (
        <svg viewBox={VIEWBOX_MAP[size]} className={styles.svg}>
          <polyline className={styles.checkmarkPath} stroke='#fff' strokeLinejoin='round' strokeLinecap='round' strokeWidth='2.5' points={POLYLINE_MAP[size]} />
        </svg>
      )
    )
  }

  const renderLabelText = (): JSX.Element => {
    return (
      <label htmlFor={id} className={labelClasses} {...(hasTestId && { 'data-testid': `${testNameSpace}-label` })}>
        <span className={styles.checkmark} role='presentation'>
          {renderTick()}
        </span>

        {label !== '' && (
          <span className={textClasses}>
            <Span size={TEXT_MAP[size]} testNameSpace={testNameSpace}>
              {isCallable(label) ? label() : label}
            </Span>
          </span>
        )}
      </label>
    )
  }

  const renderInput = (): JSX.Element => {
    return (
      <input
        {...otherProps}
        id={id}
        ref={ref}
        type='radio'
        name={name}
        className={styles.radio}
        disabled={disabled}
        aria-checked={isChecked}
        checked={isChecked}
        onChange={handleChange}
        onClick={handleClick}
        value={value}
        {...(hasTestId && { 'data-testid': `${testNameSpace}-input` })}
      />
    )
  }

  const Component = (
    <div className={containerClassNames} ref={itemRef}>
      {renderInput()}
      {renderLabelText()}
    </div>
  )

  if (kind === KIND.OPTION) {
    return <ShellOption override={optionOverride} component={Component} testNameSpace='radio' />
  }

  return Component
})
